import { utc } from '@date-fns/utc';
import { Button } from '@metservicedev/met-ui';
import { format } from 'date-fns';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiVona, AviationColorCodeText } from '@vona/shared';
import { ActivityStatusText } from '@vona/shared/Activity';
import { CloudMovementText } from '@vona/shared/CloudMovement';
import { DurationTypeText, isValueDuration } from '@vona/shared/Duration';
import { HeightFromTypeTacText, HeightUnitText } from '@vona/shared/Height';
import { StatusText } from '@vona/shared/Status';

import { createStyles } from '../tss';
import { ColorCodeOrb } from './ColorCodeOrb';
import { VerticalDivider } from './VerticalDivider';

const useStyles = createStyles(({ classes, theme }) => ({
  root: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    backgroundColor: theme.color.container.default,
    borderRadius: 8,
    maxWidth: theme.breakpoints.lg,
    minWidth: theme.breakpoints.sm,
    margin: '0 auto',
    width: '100%',
    [theme.breakpoints.below('sm')]: {
      minWidth: 'unset',
    },
  },
  divider: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.below('sm')]: {
      flexDirection: 'column',
    },
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    fontWeight: 700,
    fontSize: '16px',
    gap: theme.spacing(1),
  },
  subtitle: {
    display: 'flex',
    fontSize: '12px',
    fontWeight: 400,
    width: '100%',
  },
  caption: {
    color: theme.color.text.subtle,
    fontSize: '12px',
    fontWeight: 400,
  },
  flex: {
    flex: 1,
    textAlign: 'left',
  },
  collapsed: {},
  details: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    paddingTop: theme.spacing(2),
    transition: 'height 0.3s, padding 0.3s',
    // Allow interpolation to/from "auto". Only works in Chromium 129+ (Sept 2024)
    interpolateSize: 'allow-keywords',
    [`&.${classes.collapsed}`]: {
      height: 0,
      paddingTop: 0,
    },
  },
  heading: {
    color: theme.color.text.default,
    fontSize: '12px',
    fontWeight: 700,
  },
  content: {
    color: theme.color.text.subtle,
    fontSize: '12px',
    fontWeight: 400,
  },
  reissue: {
    marginTop: theme.spacing(2),
    alignSelf: 'center',
  },
}));

type Props = {
  expanded?: boolean;
  onClick?: () => void;
  vona: ApiVona;
};

const titleCase = (str: string) => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const Vona: React.FC<Props> = ({ expanded, onClick, vona }) => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const collapsed = !expanded;
  const { cloud, duration } = vona;

  const statusText = vona.status === 'OPERATIONAL' ? '' : ` (${StatusText[vona.status]})`;
  let activity = ActivityStatusText[vona.activity.status];
  if (vona.activity.status === 'LAST_VA_EMISSION') {
    activity += ` ${format(vona.activity.lastVaEmission, 'yyyyMMdd/HHmmX', { in: utc })}`;
  }
  let cloudHeight;
  let heightSource;
  if (cloud) {
    const height = cloud.height;
    if (height) {
      const above = height.extendsAbove ? 'Above ' : '';
      const from = HeightFromTypeTacText[height.from];
      const unit = HeightUnitText[height.unit];
      cloudHeight = `${above}${height.value}${unit} ${from}`;
      heightSource = height.source;
    } else {
      cloudHeight = 'Unknown';
      heightSource = 'Unknown';
    }
  }

  const fields = {
    'Current Colour Code': AviationColorCodeText[vona.colorCode],
    'Previous Colour Code': AviationColorCodeText[vona.previousColorCode],
    Activity: activity,
    'Onset Time': /^\d/.test(vona.onsetTime)
      ? format(vona.onsetTime, 'yyyy-MM-dd HH:mmX', { in: utc })
      : titleCase(vona.onsetTime),
    Duration: isValueDuration(duration)
      ? `${duration.value} ${DurationTypeText[duration.type]}`
      : DurationTypeText[duration.type],
    'Cloud Height': cloudHeight ?? 'No cloud present',
    'Height Source': heightSource ?? 'No cloud present',
    Movement: cloud?.movement ? CloudMovementText[cloud.movement] : 'No cloud present',
    'SVO Contact Details': vona.contacts,
    Remarks: vona.remarks || '-',
    'Next Notice': vona.nextNotice
      ? `Will be issued by ${format(vona.nextNotice, 'yyyy-MM-dd HH:mmX', { in: utc })}`
      : 'Will be issued if conditions change significantly or if the colour code changes',
  };
  const allFields = Object.entries(fields);
  const columnSize = Math.ceil(allFields.length / 2);

  return (
    <button
      className={classes.root}
      data-testid="button-vona-summary"
      onClick={onClick}
      type="button"
    >
      <div className={classes.row}>
        <div className={classes.column}>
          <div className={classes.title} data-testid="volcano-name">
            <ColorCodeOrb colorCode={vona.colorCode} data-testid="vona-color-code" />
            {vona.volcano.name} ({vona.volcano.number})
          </div>
          <div className={classes.subtitle}>
            <div className={classes.flex} data-testid="vona-issued-at">
              Issued: {format(vona.issuedAt, 'yyyy-MM-dd HH:mmX', { in: utc })}
            </div>
            <div data-testid="vona-notice-number">{vona.noticeNumber}{statusText}</div>
          </div>
          <div className={classes.subtitle}>By: {vona.svo}</div>
        </div>
        <VerticalDivider className={classes.divider} />
        <div className={classes.column}>
          <div className={classes.caption} data-testid="vona-location">
            {vona.volcano.coordinates} - {vona.volcano.area}
          </div>
        </div>
      </div>
      <div
        className={cx(classes.details, { [classes.collapsed]: collapsed })}
        data-testid="vona-details"
        data-collapsed={collapsed}
      >
        <div className={classes.row}>
          <div className={classes.column}>
            {allFields.slice(0, columnSize).map(([key, value]) => (
              <React.Fragment key={key}>
                <h4 className={classes.heading}>{key}</h4>
                <p className={classes.content}>{value}</p>
              </React.Fragment>
            ))}
          </div>
          <div className={classes.column}>
            {allFields.slice(columnSize).map(([key, value]) => (
              <React.Fragment key={key}>
                <h4 className={classes.heading}>{key}</h4>
                <p className={classes.content}>{value}</p>
              </React.Fragment>
            ))}
          </div>
        </div>
        <Button
          className={classes.reissue}
          data-testid="reissue-button"
          onClick={() => navigate(`/edit/${vona.volcano.number}`)}
          variant="secondary"
        >
          Reissue
        </Button>
      </div>
    </button>
  );
};
